import { useState, useEffect, useRef } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { Link } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import ReactDOM from "react-dom";
import { sectionPath } from "../utils/routePaths";
import { resolveSlug } from "../utils/slugify";
import { optimizedImageUrl } from "../utils/cloudinaryUrl";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const ACCENT = "#C47C2B";
const DARK = "#1A1A18";

const getCategoryColor = (category) => {
  if (!category) return "#2D4A3E";
  let hash = 0;
  for (let i = 0; i < category.length; i++) {
    hash = category.charCodeAt(i) + ((hash << 5) - hash);
  }
  const colors = [
    "#2d7a4f","#1a6b8a","#c47c2b","#7c3aed","#ba1a1a",
    "#0369a1","#854d0e","#be185d","#0f766e","#4338ca",
    "#b45309","#0e7490","#15803d","#7e22ce","#b91c1c","#0284c7",
  ];
  return colors[Math.abs(hash) % colors.length];
};

/* ─────────────────────────────────────────────
   Shared map logic extracted into a hook so both
   the desktop card and the mobile modal can reuse it
───────────────────────────────────────────── */
function useMapSetup({ mapRef, places, onSelectPlace, selectedCategory }) {
  const mapInstanceRef = useRef(null);
  const markersRef     = useRef([]);

  // init map
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current || places.length === 0) return;

    const map = L.map(mapRef.current, {
      center: [43.05, 42.9],
      zoom: 10,
      zoomControl: false,
      scrollWheelZoom: true,
    });
    mapInstanceRef.current = map;

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 19,
    }).addTo(map);

    L.control.zoom({ position: "bottomright" }).addTo(map);

    const svanetiCoords = [
      [43.15,42.15],[43.28,42.35],[43.35,42.65],[43.3,42.9],
      [43.2,43.05],[43.05,43.2],[42.9,43.05],[42.8,42.85],
      [42.75,42.55],[42.8,42.3],[42.95,42.15],[43.15,42.15],
    ];
    L.polygon(svanetiCoords, {
      color: "#C47C2B", weight: 3, opacity: 0.8,
      fillColor: "#C47C2B", fillOpacity: 0.06,
      dashArray: "8, 6", lineJoin: "round",
    }).addTo(map);

    const createIcon = (color, imageUrl) => {
      if (imageUrl) {
        return L.divIcon({
          className: "",
          html: `<div style="width:40px;height:40px;border-radius:50%;border:3px solid #fff;box-shadow:0 4px 12px rgba(0,0,0,0.3);overflow:hidden;cursor:pointer"><img src="${optimizedImageUrl(imageUrl, 80)}" style="width:100%;height:100%;object-fit:cover"/></div>`,
          iconSize: [40,40], iconAnchor: [20,20],
        });
      }
      return L.divIcon({
        className: "",
        html: `<div style="width:14px;height:14px;background:${color};border:2.5px solid #fff;border-radius:50%;box-shadow:0 2px 8px rgba(0,0,0,0.3);cursor:pointer"></div>`,
        iconSize: [14,14], iconAnchor: [7,7],
      });
    };

    places.forEach((place) => {
      const marker = L.marker([place.lat, place.lng], {
        icon: createIcon(getCategoryColor(place.category), place.image),
      });
      marker.on("click", () => onSelectPlace(place));
      marker.addTo(map);
      markersRef.current.push({ marker, place });
    });

    if (places.length > 0) {
      map.fitBounds(L.latLngBounds(places.map((p) => [p.lat, p.lng])), { padding: [60,60] });
    }

    return () => {
      map.remove();
      mapInstanceRef.current = null;
      markersRef.current = [];
    };
  }, [mapRef.current, places]);

  // filter markers by category
  useEffect(() => {
    if (!mapInstanceRef.current) return;
    markersRef.current.forEach(({ marker, place }) => {
      if (!selectedCategory || place.category === selectedCategory)
        marker.addTo(mapInstanceRef.current);
      else marker.remove();
    });
  }, [selectedCategory]);

  // invalidate size when container becomes visible (needed for modal)
  const invalidate = () => {
    setTimeout(() => mapInstanceRef.current?.invalidateSize(), 50);
  };

  return { invalidate };
}

/* ─────────────────────────────────────────────
   MapContent — the actual map + filters + popup
   rendered inside both desktop panel and mobile modal
───────────────────────────────────────────── */
function MapContent({ places, categories, language, t }) {
  const mapRef           = useRef(null);
  const [selectedPlace,    setSelectedPlace]    = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);

  useMapSetup({ mapRef, places, onSelectPlace: setSelectedPlace, selectedCategory });

  return (
    <div style={{ position: "relative", height: "100%", width: "100%", minHeight: 400 }}>
      <div ref={mapRef} style={{ height: "100%", width: "100%", minHeight: 400, background: "#e8e4dc" }} />

      {/* Category filter pills */}
      <div style={{
        position: "absolute", bottom: 16, left: 16, right: 16, zIndex: 1000,
        display: "flex", gap: 8, flexWrap: "wrap",
      }}>
        <button
          onClick={() => setSelectedCategory(null)}
          style={{
            display: "flex", alignItems: "center", gap: 5,
            padding: "6px 14px", borderRadius: 999, border: "none",
            background: !selectedCategory ? DARK : "#fff",
            color: !selectedCategory ? "#fff" : DARK,
            fontFamily: "Geist, sans-serif", fontSize: 12, fontWeight: 700,
            cursor: "pointer", boxShadow: "0 2px 8px rgba(0,0,0,0.15)", transition: "all 0.2s",
          }}
        >
          {language === "ka" ? "ყველა" : "All"}
        </button>
        {categories.slice(0, 5).map((cat) => {
          const isActive = selectedCategory === cat.name;
          const name = language === "ka" && cat.nameGeo ? cat.nameGeo : cat.name;
          return (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(isActive ? null : cat.name)}
              style={{
                display: "flex", alignItems: "center", gap: 6,
                padding: "6px 14px", borderRadius: 999, border: "none",
                background: isActive ? DARK : "#fff",
                color: isActive ? "#fff" : DARK,
                fontFamily: "Geist, sans-serif", fontSize: 12, fontWeight: 600,
                cursor: "pointer", boxShadow: "0 2px 8px rgba(0,0,0,0.12)", transition: "all 0.2s",
              }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 13, fontVariationSettings: "'FILL' 1" }}>
                {cat.icon || "place"}
              </span>
              {name}
            </button>
          );
        })}
      </div>

      {/* Selected place popup */}
      {selectedPlace && (
        <div style={{
          position: "absolute", top: 16, left: 16, width: 240,
          background: "#fff", borderRadius: 16, overflow: "hidden",
          boxShadow: "0 20px 60px rgba(0,0,0,0.15)", zIndex: 1000,
          animation: "popIn 0.2s ease",
        }}>
          <button
            onClick={() => setSelectedPlace(null)}
            style={{
              position: "absolute", top: 8, right: 8, zIndex: 2,
              background: "rgba(0,0,0,0.5)", border: "none", borderRadius: "50%",
              width: 26, height: 26, display: "flex", alignItems: "center",
              justifyContent: "center", cursor: "pointer", color: "#fff",
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 14 }}>close</span>
          </button>
          <div style={{ height: 120, background: "#e8e4dc", position: "relative" }}>
            {selectedPlace.image
              ? <img src={optimizedImageUrl(selectedPlace.image, 400)} alt={selectedPlace.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <span className="material-symbols-outlined" style={{ fontSize: 36, color: "#8C8A82" }}>landscape</span>
                </div>
            }
          </div>
          <div style={{ padding: "12px 14px 14px" }}>
            <h3 style={{ fontFamily: "Geist, sans-serif", fontSize: 15, fontWeight: 700, color: DARK, margin: "0 0 10px" }}>
              {language === "ka" && selectedPlace.nameKa ? selectedPlace.nameKa : selectedPlace.name}
            </h3>
            <Link
              to={sectionPath(language, "place", resolveSlug(selectedPlace, language))}
              style={{
                display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                background: DARK, color: "#fff", padding: "9px 0", borderRadius: 8,
                fontFamily: "Geist, sans-serif", fontSize: 12, fontWeight: 700, textDecoration: "none",
              }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 14 }}>explore</span>
              {t("map_view_details") || "View Details"}
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   Mobile fullscreen map modal
───────────────────────────────────────────── */
function MobileMapModal({ open, onClose, places, categories, language, t }) {
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleBack = (e) => {
      e.preventDefault();
      onClose();
    };
    window.addEventListener("popstate", handleBack);
    return () => window.removeEventListener("popstate", handleBack);
  }, [open, onClose]);

  if (!open) return null;

  // Use a portal so the modal escapes any parent stacking context
  return ReactDOM.createPortal(
    <div style={{
      position: "fixed",
      top: 0, left: 0, right: 0, bottom: 0,
      zIndex: 999999,
      display: "flex",
      flexDirection: "column",
      background: "var(--bg-surface)",
    }}>
      {/* Header */}
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "14px 16px",
        borderBottom: "1px solid var(--border)",
        flexShrink: 0,
        background: "var(--bg-surface)",
        zIndex: 2,
        minHeight: 56,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span className="material-symbols-outlined" style={{ fontSize: 20, color: ACCENT }}>map</span>
          <span style={{ fontFamily: "Geist, sans-serif", fontSize: 15, fontWeight: 700, color: "var(--text-primary)" }}>
            {language === "ka" ? "ინტერაქტიური რუქა" : "Interactive Map"}
          </span>
        </div>
        <button
          onClick={onClose}
          style={{
            width: 40, height: 40, borderRadius: "50%",
            background: "var(--bg-surface-alt)", border: "none",
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer", flexShrink: 0,
          }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: 22, color: "var(--text-primary)" }}>close</span>
        </button>
      </div>

      {/* Map fills remaining space */}
      <div style={{ flex: 1, position: "relative", overflow: "hidden" }}>
        <MapContent places={places} categories={categories} language={language} t={t} />
      </div>
    </div>,
    document.body
  );
}

/* ─────────────────────────────────────────────
   Main component
───────────────────────────────────────────── */
export default function InteractiveMap() {
  const [places,    setPlaces]    = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [mapReady,  setMapReady]  = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const sectionRef = useRef(null);
  const { language, t } = useLanguage();

  // lazy-load map when section scrolls into view
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setMapReady(true); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    getDocs(collection(db, "categories"))
      .then((snap) => setCategories(snap.docs.map((d) => ({ id: d.id, ...d.data() }))))
      .catch(console.error);
  }, []);

  useEffect(() => {
    getDocs(collection(db, "places"))
      .then((snap) => setPlaces(
        snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((p) => p.lat && p.lng)
      ))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  return (
    <section ref={sectionRef} style={{ background: "transparent", padding: "40px 5vw 60px" }}>

      {/* ── DESKTOP / TABLET: original side-by-side card ── */}
      <div className="map-desktop-card" style={{
        background: "var(--bg-surface)", borderRadius: 24, overflow: "hidden",
        boxShadow: "0 4px 32px rgba(0,0,0,0.1)",
        border: "1.5px solid var(--border)",
        display: "grid", gridTemplateColumns: "1fr 340px",
      }}>
        {/* Left — Map */}
        <div style={{ position: "relative", minHeight: 480 }}>
          {mapReady && !loading
            ? <MapContent places={places} categories={categories} language={language} t={t} />
            : (
              <div style={{
                height: 480, background: "#e8e4dc",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <div style={{
                  width: 40, height: 40, borderRadius: "50%",
                  border: "3px solid rgba(26,26,24,0.1)", borderTopColor: ACCENT,
                  animation: "mapSpin 0.8s linear infinite",
                }} />
              </div>
            )
          }
        </div>

        {/* Right — Text panel */}
        <div style={{
          padding: "48px 40px", display: "flex", flexDirection: "column",
          justifyContent: "center", borderLeft: "1px solid var(--border-soft)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 16 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path d="M2 22L10 8L14 14L18 10L22 22Z" stroke={ACCENT} strokeWidth="2.2" strokeLinejoin="round" fill="none"/>
            </svg>
            <span style={{ fontFamily: "Geist, sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--accent-text)" }}>
              {t("map_eyebrow") || (language === "ka" ? "ინტერაქტიური რუქა" : "Interactive Map")}
            </span>
          </div>
          <h2 style={{ fontFamily: "Geist, sans-serif", fontSize: "clamp(28px, 2.8vw, 40px)", fontWeight: 700, letterSpacing: "-0.02em", color: "var(--text-primary)", lineHeight: 1.1, marginBottom: 16 }}>
            {t("map_title") || (language === "ka" ? "გამოიკვლიე სვანეთი შენებურად" : <>Explore Svaneti<br />Your Way</>)}
          </h2>
          <p style={{ fontFamily: "Geist, sans-serif", fontSize: 15, color: "var(--text-secondary)", lineHeight: 1.7, marginBottom: 32 }}>
            {language === "ka"
              ? "იპოვე ყველა ლოკაცია, დაგეგმე მარშრუტი და შეისწავლე სვანეთი სიმარტივით."
              : "Find all locations, plan your route, and explore Svaneti with ease."}
          </p>
          <Link
            to={sectionPath(language, "categories")}
            style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "#2D4A3E", color: "#fff", padding: "13px 22px", borderRadius: 10, fontFamily: "Geist, sans-serif", fontSize: 14, fontWeight: 600, textDecoration: "none", width: "fit-content", transition: "all 0.2s" }}
            onMouseEnter={(e) => e.currentTarget.style.background = "#3a5c4e"}
            onMouseLeave={(e) => e.currentTarget.style.background = "#2D4A3E"}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 17 }}>location_on</span>
            {language === "ka" ? "ინტერაქტიური რუქა" : "Open Interactive Map"}
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>arrow_forward</span>
          </Link>
        </div>
      </div>

      {/* ── MOBILE: preview card + open button ── */}
      <div className="map-mobile-card" style={{
        background: "var(--bg-surface)", borderRadius: 20, overflow: "hidden",
        boxShadow: "0 4px 24px rgba(0,0,0,0.1)",
        border: "1.5px solid var(--border)",
      }}>
        {/* Static preview image / placeholder */}
        <div style={{ position: "relative", height: 180, background: "#D6D0C8", overflow: "hidden" }}>
          {/* Decorative map-like background */}
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(135deg, #e8e0d0 0%, #d4cbbf 50%, #c8bfb0 100%)" }} />
          {/* Grid lines to mimic a map */}
          <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0.3 }} viewBox="0 0 300 180">
            {[30,60,90,120,150,180,210,240,270].map(x => <line key={x} x1={x} y1="0" x2={x} y2="180" stroke="#8C8A82" strokeWidth="0.5"/>)}
            {[30,60,90,120,150].map(y => <line key={y} x1="0" y1={y} x2="300" y2={y} stroke="#8C8A82" strokeWidth="0.5"/>)}
            {/* Sample dots */}
            <circle cx="100" cy="70"  r="6" fill="#C47C2B" opacity="0.8"/>
            <circle cx="160" cy="50"  r="5" fill="#2D4A3E" opacity="0.8"/>
            <circle cx="200" cy="100" r="7" fill="#1A6B8A" opacity="0.8"/>
            <circle cx="130" cy="120" r="5" fill="#7c3aed" opacity="0.8"/>
            <circle cx="70"  cy="110" r="4" fill="#854d0e" opacity="0.8"/>
            {/* Svaneti-like polygon outline */}
            <polyline points="40,40 80,25 140,20 200,35 240,60 250,100 220,140 160,155 100,145 60,120 40,80 40,40"
              fill="none" stroke="#C47C2B" strokeWidth="1.5" strokeDasharray="6,4" opacity="0.6"/>
          </svg>

          {/* Overlay label */}
          <div style={{
            position: "absolute", inset: 0,
            display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center", gap: 8,
          }}>
            <div style={{
              width: 48, height: 48, borderRadius: "50%",
              background: "rgba(255,255,255,0.9)",
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
            }}>
              <span className="material-symbols-outlined" style={{ fontSize: 24, color: ACCENT }}>map</span>
            </div>
            <span style={{
              fontFamily: "Geist, sans-serif", fontSize: 12, fontWeight: 600,
              color: DARK, background: "rgba(255,255,255,0.85)",
              padding: "4px 12px", borderRadius: 999,
            }}>
              {places.length > 0
                ? (language === "ka" ? `${places.length} ადგილი რუქაზე` : `${places.length} places on map`)
                : (language === "ka" ? "რუქა" : "Map")}
            </span>
          </div>

          {/* Category dot previews bottom-left */}
          {categories.slice(0, 4).length > 0 && (
            <div style={{
              position: "absolute", bottom: 10, left: 12,
              display: "flex", gap: 6, alignItems: "center",
            }}>
              {categories.slice(0, 4).map((cat) => (
                <div key={cat.id} style={{
                  display: "flex", alignItems: "center", gap: 4,
                  background: "rgba(255,255,255,0.88)", backdropFilter: "blur(8px)",
                  padding: "3px 8px", borderRadius: 999,
                  fontFamily: "Geist, sans-serif", fontSize: 10, fontWeight: 600, color: DARK,
                }}>
                  <span className="material-symbols-outlined" style={{ fontSize: 11, fontVariationSettings: "'FILL' 1" }}>
                    {cat.icon || "place"}
                  </span>
                  {language === "ka" && cat.nameGeo ? cat.nameGeo : cat.name}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Bottom text + button */}
        <div style={{ padding: "16px 16px 18px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 4 }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                <path d="M2 22L10 8L14 14L18 10L22 22Z" stroke={ACCENT} strokeWidth="2.2" strokeLinejoin="round" fill="none"/>
              </svg>
              <span style={{ fontFamily: "Geist, sans-serif", fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--accent-text)" }}>
                {language === "ka" ? "ინტერაქტიური რუქა" : "Interactive Map"}
              </span>
            </div>
            <p style={{ fontFamily: "Geist, sans-serif", fontSize: 13, fontWeight: 700, color: "var(--text-primary)", margin: 0, lineHeight: 1.3 }}>
              {language === "ka" ? "იპოვინე სასურველი ადგილი" : "Find your place"}
            </p>
            <p style={{ fontFamily: "Geist, sans-serif", fontSize: 11, color: "var(--text-secondary)", margin: "2px 0 0", lineHeight: 1.4 }}>
              {language === "ka" ? "შეეხე გასახსნელად" : "Tap to explore"}
            </p>
          </div>
          <button
            onClick={() => setModalOpen(true)}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              background: "#2D4A3E", color: "#fff",
              padding: "11px 16px", borderRadius: 10,
              fontFamily: "Geist, sans-serif", fontSize: 13, fontWeight: 600,
              border: "none", cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0,
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>map</span>
            {language === "ka" ? "რუკის გახსნა" : "Open Map"}
          </button>
        </div>
      </div>

      {/* Mobile fullscreen modal */}
      <MobileMapModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        places={places}
        categories={categories}
        language={language}
        t={t}
      />

      <style>{`
        @keyframes mapSpin  { to { transform: rotate(360deg); } }
        @keyframes popIn    { from { opacity:0; transform:scale(0.9); } to { opacity:1; transform:scale(1); } }
        @keyframes fadeIn   { from { opacity:0; } to { opacity:1; } }
        @keyframes slideUp  { from { transform:translateY(40px); opacity:0; } to { transform:translateY(0); opacity:1; } }
        .leaflet-container { font-family: "Geist, sans-serif" !important; }
        .leaflet-popup     { display: none !important; }

        /* Desktop: show map card, hide mobile card */
        .map-desktop-card { display: grid; }
        .map-mobile-card  { display: none; }

        @media (max-width: 900px) {
          .map-desktop-card { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 640px) {
          .map-desktop-card { display: none !important; }
          .map-mobile-card  { display: block !important; }
        }
      `}</style>
    </section>
  );
}
